import { useLayoutEffect, useMemo, useRef, useState } from 'react'

interface UseIntersectionObserverOptions {
  defaultVisible?: boolean
  targetKey?: any
  cb?: (entries: IntersectionObserverEntry[]) => void
  triggerOnce?: boolean
  rootMargin?: string
  rootClassName?: string
  threshold?: number | number[]
}

interface UseIntersectionObserverResult<
  T extends HTMLElement = HTMLDivElement,
  C extends HTMLElement = HTMLDivElement
> {
  ref: React.MutableRefObject<T | null>
  containerRef: React.MutableRefObject<C | null>
  visible: boolean
  ratio: number
}

export const useIntersectionObserver = <
  T extends HTMLElement = HTMLDivElement,
  C extends HTMLElement = HTMLDivElement
>({
  defaultVisible = true,
  targetKey = null,
  cb = (() => {}) as (entries: IntersectionObserverEntry[]) => void,
  triggerOnce = false,
  rootMargin,
  rootClassName = '',
  threshold,
}: UseIntersectionObserverOptions = {}): UseIntersectionObserverResult<T, C> => {
  const ref = useRef<T | null>(null)
  const containerRef = useRef<C | null>(null)
  const [visible, setVisible] = useState(defaultVisible ?? true)
  const currentVisible = useRef(visible)
  useLayoutEffect(() => {
    currentVisible.current = visible
  })

  const [ratio, setRatio] = useState(defaultVisible ?? true ? 1 : 0)
  const [disabled, setDisabled] = useState(false)

  useLayoutEffect(() => {
    if (disabled) {
      return
    }

    /**
     * @param {IntersectionObserverEntry[]} entries
     */
    const observerCb = (entries) => {
      for (const e of entries) {
        if (!triggerOnce || defaultVisible !== e.isIntersecting) {
          setVisible(e.isIntersecting)
        }
        setRatio(e.intersectionRatio)
        if (triggerOnce && currentVisible.current !== e.isIntersecting) {
          setDisabled(true)
        }
      }
      cb(entries)
    }
    if (window.IntersectionObserver) {
      let root: HTMLElement | undefined
      if (rootClassName !== '') {
        let leaf: HTMLElement | null = ref.current
        while (leaf != null) {
          if (leaf.classList.contains(rootClassName)) {
            root = leaf
            break
          } else {
            leaf = leaf.parentElement
          }
        }
      }
      if (containerRef.current) {
        root = containerRef.current
      }

      const observer = new IntersectionObserver(observerCb, {
        root,
        rootMargin,
        threshold,
      })

      if (ref.current) {
        observer.observe(ref.current)
      }

      return () => {
        observer.disconnect()
      }
    } else {
      console.error('IntersectionObserver not available')
    }
  }, [cb, defaultVisible, disabled, rootClassName, rootMargin, setDisabled, threshold, triggerOnce, targetKey])

  const result = useMemo(
    () => ({
      ref,
      containerRef,
      visible,
      ratio,
    }),
    [ratio, visible]
  )

  return result
}
